import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, CheckCircle2 } from 'lucide-react';
import { KTEK_LINES, KTEK_LINEUP_IMAGE } from '../data/ktekInfo';
import { useSeo } from '../hooks/useSeo';

function KtekOverview(): React.ReactElement {
  useSeo(
    'KTEK: toner e componenti compatibili selezionati da DTK',
    'Scopri KTEK, il marchio DTK di consumabili e componenti compatibili: linea PRO per alte prestazioni e linea SMART per la convenienza.'
  );

  return (
    <>
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-col sm:flex-row gap-8 mb-10 items-start">
          <div className="flex-1">
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight mb-6">KTEK, l&apos;alternativa DTK per la stampa professionale</h1>
            <p className="text-sm text-muted-foreground leading-relaxed">
              KTEK è il marchio DTK dedicato ai consumabili e ai componenti compatibili per la stampa. Nasce dall&apos;esperienza maturata ogni giorno a contatto con rivenditori, centri assistenza e professionisti del settore, con l&apos;obiettivo di offrire alternative affidabili, riconoscibili e adatte a differenti esigenze di utilizzo. Ogni prodotto KTEK viene selezionato secondo precisi criteri di compatibilità, qualità di stampa, affidabilità e convenienza.
            </p>
          </div>
          <div className="w-full sm:w-96 flex-shrink-0 rounded-lg overflow-hidden">
            <img src={KTEK_LINEUP_IMAGE} alt="Linee KTEK PRO e KTEK SMART di toner e componenti compatibili" className="w-full h-auto" data-testid="ktek-lineup-image" />
          </div>
        </div>

        <h2 className="text-lg font-semibold mb-6">Due linee, una stessa competenza</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
          {KTEK_LINES.map(line => (
            <Link
              key={line.slug}
              to={`/ktek/${line.slug}`}
              className="group p-6 rounded-lg border bg-white hover:shadow-md transition-shadow"
              data-testid={`ktek-line-card-${line.slug}`}
              style={{ borderColor: line.accentColor + '30' }}
            >
              <h3 className="text-lg font-bold mb-2" style={{ color: line.accentColor }}>{line.label}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed mb-3">
                {line.slug === 'pro'
                  ? 'La linea per chi cerca alte prestazioni, continuità e affidabilità anche negli utilizzi più intensivi.'
                  : 'La linea per chi desidera contenere i costi di stampa e ottenere un equilibrio efficace tra affidabilità e convenienza.'}
              </p>
              <span className="flex items-center gap-1 text-xs font-semibold group-hover:underline" style={{ color: line.accentColor }}>
                Scopri {line.label} <ArrowRight className="w-3 h-3" />
              </span>
            </Link>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row gap-8 mb-10 items-start">
          <div className="flex-1">
            <h2 className="text-lg font-semibold mb-6">Una scelta più semplice</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              La distinzione tra PRO e SMART permette al rivenditore e all&apos;utilizzatore professionale di individuare con immediatezza la proposta più coerente con volumi di stampa, aspettative prestazionali e obiettivi di costo.
            </p>
          </div>
          <div className="w-full sm:w-96 flex-shrink-0 flex gap-3">
            {KTEK_LINES.map(line => (
              <div key={line.slug} className="flex-1 rounded-lg overflow-hidden aspect-video bg-secondary">
                <img src={line.image} alt={`${line.label} toner e consumabili compatibili`} className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
        </div>

        {/* Tabella di confronto */}
        <div className="rounded-lg border bg-white overflow-hidden mb-10" data-testid="ktek-comparison-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-secondary/60">
                <th className="text-left px-4 py-3 font-semibold">Caratteristica</th>
                <th className="text-left px-4 py-3 font-semibold" style={{ color: KTEK_LINES[0].accentColor }}>{KTEK_LINES[0].label}</th>
                <th className="text-left px-4 py-3 font-semibold" style={{ color: KTEK_LINES[1].accentColor }}>{KTEK_LINES[1].label}</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              <tr>
                <td className="px-4 py-3 text-muted-foreground">Posizionamento</td>
                <td className="px-4 py-3">{KTEK_LINES[0].positioning}</td>
                <td className="px-4 py-3">{KTEK_LINES[1].positioning}</td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-muted-foreground">Priorità</td>
                <td className="px-4 py-3">{KTEK_LINES[0].priority}</td>
                <td className="px-4 py-3">{KTEK_LINES[1].priority}</td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-muted-foreground">Promessa</td>
                <td className="px-4 py-3 italic">&ldquo;{KTEK_LINES[0].promise}&rdquo;</td>
                <td className="px-4 py-3 italic">&ldquo;{KTEK_LINES[1].promise}&rdquo;</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="text-sm text-muted-foreground leading-relaxed max-w-4xl">
          Due linee, una garanzia comune: la competenza DTK nella selezione dei prodotti. Scegli KTEK PRO quando prestazioni e continuità sono prioritarie; scegli KTEK SMART quando l&apos;obiettivo principale è contenere il costo di stampa mantenendo l&apos;affidabilità necessaria per il lavoro quotidiano.
        </p>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 pb-10">
        <div className="relative overflow-hidden rounded-lg bg-gradient-to-r from-[#1E4D8C] to-[#2B5EA7] p-8 sm:p-10 flex flex-col sm:flex-row items-center gap-6 shadow-lg" data-testid="ktek-cta">
          <div className="flex-1">
            <h2 className="text-lg sm:text-xl font-semibold text-white mb-1.5">Sei un rivenditore?</h2>
            <p className="text-white/80 text-sm">
              Registrati per accedere al catalogo completo con prezzi riservati, disponibilità in tempo reale e ordini online.
            </p>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <Link to="/registrati" data-testid="ktek-register" className="h-11 px-6 rounded-md bg-gradient-to-r from-amber-300 to-amber-400 text-[#1E4D8C] text-sm font-bold flex items-center gap-2 hover:from-amber-400 hover:to-amber-500 hover:scale-[1.03] shadow-sm hover:shadow-md transition-all">
              Registrati ora
            </Link>
            <Link to="/accedi" data-testid="ktek-login" className="h-11 px-6 rounded-md border-2 border-white/50 text-white text-sm font-medium flex items-center hover:border-amber-400 hover:bg-amber-400 hover:text-[#1E4D8C] transition-colors">
              Accedi
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}

function KtekLineDetail({ slug }: { slug: 'pro' | 'smart' }): React.ReactElement {
  const line = KTEK_LINES.find(l => l.slug === slug)!;
  const otherLine = KTEK_LINES.find(l => l.slug !== slug)!;
  useSeo(line.seoTitle, line.metaDescription);

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-8">
      <Link to="/ktek" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors" data-testid="ktek-line-back">
        <ArrowLeft className="w-3.5 h-3.5" /> Torna a KTEK
      </Link>

      <div className="flex flex-col sm:flex-row gap-8 mb-10">
        <div className="flex-1 max-w-2xl">
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight mb-6" style={{ color: line.accentColor }}>{line.heading}</h1>
          <p className="text-sm text-muted-foreground leading-relaxed mb-8">{line.intro}</p>

          <h2 className="text-base font-semibold mb-4">{line.targetTitle}</h2>
          <ul className="space-y-1.5 mb-8">
            {line.target.map((t, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: line.accentColor }} />
                {t}
              </li>
            ))}
          </ul>

          <h2 className="text-base font-semibold mb-4">{line.benefitsTitle}</h2>
          <ul className="space-y-1.5 mb-8">
            {line.benefits.map((b, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: line.accentColor }} />
                {b}
              </li>
            ))}
          </ul>

          <Link
            to={`/ktek/${otherLine.slug}`}
            className="inline-flex items-center gap-1.5 text-sm font-semibold hover:underline"
            style={{ color: otherLine.accentColor }}
            data-testid="ktek-switch-line"
          >
            Scopri anche {otherLine.label} <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
        <div className="w-full sm:w-72 flex-shrink-0">
          <div className="rounded-md overflow-hidden aspect-video bg-secondary">
            <img src={line.image} alt={`${line.label} toner e consumabili compatibili`} className="w-full h-full object-cover" data-testid="ktek-line-image" />
          </div>
        </div>
      </div>

      <div className="relative overflow-hidden rounded-lg bg-gradient-to-r from-[#1E4D8C] to-[#2B5EA7] p-8 sm:p-10 flex flex-col sm:flex-row items-center gap-6 shadow-lg" data-testid="ktek-line-cta">
        <div className="flex-1">
          <h2 className="text-lg sm:text-xl font-semibold text-white mb-1.5">Sei un rivenditore?</h2>
          <p className="text-white/80 text-sm">
            Registrati per accedere al catalogo completo con prezzi riservati, disponibilità in tempo reale e ordini online.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <Link to="/registrati" data-testid="ktek-line-register" className="h-11 px-6 rounded-md bg-gradient-to-r from-amber-300 to-amber-400 text-[#1E4D8C] text-sm font-bold flex items-center gap-2 hover:from-amber-400 hover:to-amber-500 hover:scale-[1.03] shadow-sm hover:shadow-md transition-all">
            Registrati ora
          </Link>
          <Link to="/accedi" data-testid="ktek-line-login" className="h-11 px-6 rounded-md border-2 border-white/50 text-white text-sm font-medium flex items-center hover:border-amber-400 hover:bg-amber-400 hover:text-[#1E4D8C] transition-colors">
            Accedi
          </Link>
        </div>
      </div>
    </div>
  );
}

/** Pagine del marchio proprietario KTEK: panoramica (`/ktek`) e linee
 * PRO/SMART (`/ktek/pro`, `/ktek/smart`). Contenuto ripreso dal documento
 * "DTK_Contenuti_Web_SEO_v1" fornito dall'utente. */
export default function KtekPage(): React.ReactElement {
  const { pageId } = useParams<{ pageId?: string }>();

  if (!pageId) return <KtekOverview />;
  if (pageId === 'pro' || pageId === 'smart') return <KtekLineDetail slug={pageId} />;
  return <Navigate to="/ktek" replace />;
}
